<template>
  <v-container fluid>
    <v-row v-if="showHeaderPage">
      <v-col cols="12" md="3" align="start">
        <p class="base-title">Informações do Plano</p>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col cols="12" md="3">
        <label class="label">Contrato / Apólice</label>
        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-autocomplete
                v-model="contractedPlan.contractId"
                :items="contracts"
                item-value="id"
                item-text="text"
                class="mt-2"
                placeholder="Selecione"
                outlined
                color="primary"
                :loading="loadingContracts || loadingPlanInformation || loadingForm"
                @input="loadSubContracts()"
                :rules="isReuseMovement ? [rule.required] : []"
                :disabled="!isReuseMovement || loadingPlanInformation || loadingForm"
                clearable
              />
            </div>
          </template>
          <span>{{ captureContractText() }}</span>
        </v-tooltip>
      </v-col>
      <v-col cols="12" md="3">
        <label class="label">Sub-Contrato</label>
        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-autocomplete
                v-model="contractedPlan.subContractId"
                :items="subContracts"
                item-text="text"
                item-value="id"
                class="mt-2"
                placeholder="Selecione"
                outlined
                color="primary"
                :loading="loadingSubContracts || loadingPlanInformation || isDisabledSubContracts || loadingForm"
                :disabled="isDetail && !isReuseMovement || loadingPlanInformation || isDisabledSubContracts || loadingForm"
                :rules="isReuseMovement ? [rule.required] : []"
                @input="onInputSubContract"
                clearable
              />
            </div>
          </template>
        <span>{{ captureSubContractText() }}</span>
        </v-tooltip>
      </v-col>
      <v-col cols="12" md="3">
        <label class="label">
          {{ contractedPlan.units && contractedPlan.units.length > 0 ? 'Unidade*' : 'Unidade' }}
        </label>
        <v-row no-gutters class="mt-2">
          <v-col class="flex-grow-0 flex-shrink-0">
            <v-tooltip top color="primary">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-autocomplete
                    class="rounded-r-0"
                    v-model="contractedPlan.unit"
                    :items="contractedPlan.units"
                    @change="setUnit(contractedPlan, $event)"
                    item-text="code"
                    item-value="id"
                    placeholder="Código"
                    style="min-width: 120px;"
                    maxlength="70"
                    outlined
                    color="primary"
                    :disabled="isDisabledUnitField"
                    :loading="loadingUnits"
                    :rules="contractedPlan.units && contractedPlan.units.length > 0 ? [rule.required] : []"
                    validate-on-blur
                    return-object
                    clearable
                  />
                </div>
              </template>
              <span>{{ formatUnityTooltip(contractedPlan.unit, contractedPlan.unitCode, 'code') }}</span>
            </v-tooltip>
          </v-col>
          <v-col class="flex-grow-1 flex-shrink-1">
            <v-tooltip top color="primary">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-text-field
                    class="rounded-l-0"
                    :value="contractedPlan.unitDescription || ''"
                    placeholder="Descrição"
                    maxlength="70"
                    outlined
                    color="primary"
                    disabled
                  />
                </div>
              </template>
              <span>{{ formatUnityTooltip(contractedPlan.unit, contractedPlan.unitDescription, 'description') }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
      <v-col v-if="showEligibility" cols="12" md="3">
        <label class="label">Elegibilidade</label>
        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-autocomplete
                v-model="contractedPlan.eligibilityId"
                :items="eligibilities"
                item-text="elegibility_id.name"
                item-value="id"
                class="mt-2"
                placeholder="Selecione"
                outlined
                color="primary"
                :loading="loadingEligibilities || loadingForm || loadingPlanInformation || isDisabledEligibilites"
                :disabled="isDetail && !isReuseMovement || loadingForm || loadingPlanInformation || isDisabledEligibilites"
                :rules="isReuseMovement && eligibilities && eligibilities.length > 0 ? [rule.required] : []"
                @input="loadPlans(); contractedPlan.planId = ''; observerContractedPlan()"
                clearable
              />
            </div>
          </template>
          <span>{{ captureEligibilityText() }}</span>
        </v-tooltip>
      </v-col>
      <v-col cols="12" md="3">
        <label class="label">Plano</label>
        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-autocomplete
                v-model="contractedPlan.planId"
                :items="plans"
                item-text="name"
                item-value="id"
                class="mt-2"
                placeholder="Selecione"
                outlined
                color="primary"
                :loading="loadingPlans || loadingPlanInformation || isDisabledPlans || loadingForm"
                @input="observerContractedPlan();"
                :rules="isReuseMovement ? [rule.required] : []"
                :disabled="isDetail && !isReuseMovement || disabledPlanInput"
                clearable
              />
            </div>
          </template>
          <span>{{ capturePlanText() }}</span>
        </v-tooltip>
        </v-col>
      <v-col v-if="showStartValidityDate" cols="12" md="3">
        <label class="label">Data Início de Vigência</label>
        <div class="d-flex flex-column">
          <v-menu
            v-model="validityDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            left
            min-width="auto">
            <template
              v-slot:activator="{ on, attrs }">
              <v-text-field
                class="mt-2"
                outlined
                :disabled="isDetail"
                v-model="contractedPlan.startValidityDate"
                append-icon="fas fa-calendar-alt"
                placeholder="DD/MM/YYYY"
                v-mask="'##/##/####'"
                color="primary"
                v-bind="attrs"
                v-on="on"
                readonly
                clearable
                @click="getAllowedDates()"
                @input="saveValidatyDate(contractedPlan.validityDate); observerContractedPlan()"
                :rules="[rule.isDateValid, rule.endDateIsGreaterThanOrEqualToStartDate(admissionDateHolder, contractedPlan.validityDate, 'Data de vigência deve ser maior ou igual a data de admissão')]"
                validate-on-blur
              />
            </template>
            <AllowedDatePicker
              @date="updateStartValidityDate($event)"
              :allowedDates="allowedDates"
            />
          </v-menu>
        </div>
      </v-col>
      <v-col v-if="showEndValidityDate" cols="12" md="3">
        <label class="label">Data Fim de Vigência*</label>
        <div class="d-flex flex-column">
          <v-menu
            v-model="validityDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            left
            min-width="auto">
            <template
              v-slot:activator="{ on, attrs }">
              <v-text-field
                class="mt-2"
                outlined
                :disabled="isDetail"
                v-model="contractedPlan.endValidityDate"
                append-icon="fas fa-calendar-alt"
                placeholder="DD/MM/YYYY"
                v-mask="'##/##/####'"
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click="getAllowedDates()"
                readonly
                clearable
              />
            </template>
            <AllowedDatePicker
              @date="updateEndValidityDate($event)"
              :allowedDates="allowedDates"
            />
          </v-menu>
        </div>
      </v-col>
      <v-col v-if="showCancellationReason" cols="12" md="3">
        <label class="label">Motivo de Exclusão</label>
        <v-text-field
          v-model="contractedPlan.cancellationReason"
          class="mt-2"
          placeholder="Não informado"
          outlined
          color="primary"
          :disabled="isDetail"
          validate-on-blur
        />
      </v-col>
      <v-col  v-if="showFamilyCode" cols="12" md="3">
        <label class="label">Código Familiar</label>
        <v-text-field
          outlined
          class="mt-2"
          v-model="contractedPlan.familyCode"
          v-mask="`${ formatter.maskLimiter(10) }`"
          color="primary"
          placeholder="Informe código familiar"
          :disabled="isDisabledFamilyCode || isDetail"
        />
      </v-col>
      <v-col  v-if="showFamilyCodeSequential" cols="12" md="3">
        <label class="label">Sequencial</label>
        <v-text-field
          outlined
          class="mt-2"
          v-model="contractedPlan.familyCodeSequential"
          v-mask="`${ formatter.maskLimiter(5) }`"
          color="primary"
          placeholder="Informe código sequencial"
          :disabled="isDisabledFamilyCodeSequential || isDetail"
        />
      </v-col>
      <v-col cols="12" md="3" v-if="showGrace">
        <label class="label">Carência</label>
        <v-autocomplete
          v-model="contractedPlan.hasGracePeriod"
          :items="graceItems"
          :disabled="isDetail || isRHProtegido"
          placeholder="Selecione"
          outlined
          color="primary"
          clearable
          class="mt-2"
          @input="observerContractedPlan();"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Rules from '@/shared/validators/formRules';
import Formatters from '@/shared/formatters/formatters';
import Product2Service from '@/services-http/contract/Product2Service';
import FinancialGroupsMixin from '@/shared/mixins/sdi/financialGroupsMixin';
import ContractsMixin from '@/shared/mixins/sdi/contractsMixin';
import EligibilitiesMixin from '@/shared/mixins/sdi/eligibilitiesMixin';
import PlansMixin from '@/shared/mixins/sdi/plansMixin';
import AllowedDatePicker from '@/components/AllowedDatePicker/AllowedDatePicker.vue';
import CarrierService from '@/services-http/contract/CarrierService';
import ContractUnitService from '@/services-http/contract/ContractUnitService';
import moment from 'moment';
import ReuseMovementMixin from '@/shared/mixins/movementRecords/reuse/ReuseMovementMixin';
import UnityMixin from '@/shared/mixins/unityMixin/unitymixin';

export default {
  components: {
    AllowedDatePicker
  },

  data: () => ({
    moment: moment,
    loadingForm: false,
    validityDateMenu: null,
    validityDatePicker: null,
    carrierService: null,
    allowedDates: [],
    loadingFinancialGroups: false,
    loadingPlanInformation: false,
    loadingContracts: false,
    loadingSubContracts: false,
    loadingEligibilities: false,
    loadingPlans: false,
    loadingUnits: false,
    isDisabledFamilyCode: false,
    isDisabledFamilyCodeSequential: false,
    isMounted: false,
    isDisabledSubContracts: false,
    isDisabledEligibilites: false,
    isDisabledPlans: false,
    contractUnitService: null,

    contractedPlan: {
      financialGroupId: null,
      contractId: null,
      subContractId: null,
      planId: null,
      familyCode: null,
      familyCodeSequential: null,
      cancellationReason: null,
      hasGracePeriod: null,
      unit: null,
      units: [],
      unitId: null,
      unitDescription: null,
      unitCode: null,
    },
    graceItems: [
      { text: 'Sim', value: true },
      { text: 'Não', value: false },
    ],

    contractName: '-',
    planName: '-',

    financialGroups: [],
    contracts: [],
    subContracts: [],
    plans: [],
    eligibilitiesIds: [],
    eligibilities: [],
    dataPlans: {},
  }),

  props: {
    contractedPlanData: {
      type: Object,
      required: true,
    },
    admissionDateHolder: {
      type: String,
      required: false,
    },
    isDetail: {
      type: Boolean,
      required: true,
    },
    showHeaderPage: {
      type: Boolean,
      required: true,
    },
    showEligibility: {
      type: Boolean,
      required: true,
    },
    showStartValidityDate: {
      type: Boolean,
      required: true,
    },
    showEndValidityDate: {
      type: Boolean,
      required: true,
    },
    showCancellationReason: {
      type: Boolean,
      required: true,
    },
    showFamilyCode: {
      type: Boolean,
      required: true,
    },
    showFamilyCodeSequential: {
      type: Boolean,
      required: true,
    },
    showGrace: {
      type: Boolean,
      required: false,
    },
    isRHProtegido: {
      type: Boolean,
      required: false,
    },
    movementNotAllowed: {
      type: Boolean,
      default: true,
    },
    loadingParent: {
      type: Boolean,
      default: false,
    }
  },

  watch: {
    contractedPlanData: {
      deep: true,
      handler(val) {
        if (val && !this.isMounted) {
          this.loadContractedPlanData();
          this.isMounted = true;
        }
      },
    },
    contractedPlan: {
      deep: true,
      async handler(val) {
        if (val) {
          await this.getPlanById();
          this.$emit('updatePlanData', val)
        }
      }
    },
    contractName: {
      deep: true,
      handler(val) {
        this.updateDataAndEmit({ contractName: val });
      },
    },
    planName: {
      deep: true,
      handler(val) {
        this.updateDataAndEmit({ planName: val });
      },
    },
  },

  mixins: [
    FinancialGroupsMixin,
    ContractsMixin,
    EligibilitiesMixin,
    PlansMixin,
    ReuseMovementMixin,
    UnityMixin,
  ],

  methods: {
    setUnit(item, event, emitChange = true) {
      item.unit = event || null;
      item.unitId = event ? event.id : null;
      item.unitCode = event ? event.code : null;
      item.unitDescription = event ? event.description : null;

      if (emitChange) {
        this.observerContractedPlan();
      }
    },
    async onInputSubContract() {
      this.resetUnit();
      await this.loadEligibilities();
      await this.loadUnits();
      this.observerContractedPlan();
    },
    async getAllowedDates() {
      const fullPath = this.$router.currentRoute.fullPath.toString();
      const movementTypeUrl = fullPath.match(/movementType=([^&]*)/)[1];
      await this.carrierService.GetAllowedDates([parseInt(this.contractedPlanData.contractId)], movementTypeUrl).then(response => {
        if (response.status === 200) {
          this.allowedDates = response.data;
        }
      })
    },
    updateStartValidityDate(value) {
      if (Boolean(this.contractedPlan)) {
        this.contractedPlan.startValidityDate = moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY');
        this.validityDateMenu = false;
      }
    },
    updateEndValidityDate(value) {
      if (Boolean(this.contractedPlan)) {
        this.contractedPlan.endValidityDate = moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY');
        this.validityDateMenu = false;
      }
    },
    updateDataAndEmit(data) {
      this.dataPlans = { ...this.dataPlans, ...data };
      this.$emit('getCombinedDataPlans', this.dataPlans);
    },
    async getUnitById(unitId) {
      if (!unitId) return null;

      try {
        const response = await this.contractUnitService.GetUnitById(unitId);
        const unit = response && response.data
          ? (Array.isArray(response.data) ? response.data[0] : response.data)
          : null;

        if (unit) {
          this.setUnit(this.contractedPlan, unit, false);
        }

        return unit;
      } catch (error) {
        return null;
      }
    },
    async getUnitByContractIdAndSubcontractId(contractId, subcontractId) {
      this.contractedPlan.units = [];

      if (!contractId || !subcontractId) return;

      const response = await this.contractUnitService.GetUnitByContractIdAndSubcontractId(
        contractId,
        subcontractId
      );

      if (response && response.data) {
        this.contractedPlan.units = response.data;
      }
    },
    async loadUnits() {
      if (!this.contractedPlan.contractId || !this.contractedPlan.subContractId) {
        this.contractedPlan.units = [];
        return;
      }

      this.loadingUnits = true;

      try {
        await this.getUnitByContractIdAndSubcontractId(
          this.contractedPlan.contractId,
          this.contractedPlan.subContractId
        );
        await this.syncSelectedUnit();
      } finally {
        this.loadingUnits = false;
      }
    },
    async syncSelectedUnit() {
      if (!this.contractedPlan.unitId) return;

      const selectedUnit = this.contractedPlan.units.find(
        (unit) => String(unit.id) === String(this.contractedPlan.unitId)
      );

      if (selectedUnit) {
        this.setUnit(this.contractedPlan, selectedUnit, false);
        return;
      }

      if (!this.contractedPlan.unitCode || !this.contractedPlan.unitDescription) {
        await this.getUnitById(this.contractedPlan.unitId);
      }
    },
    resetUnit() {
      this.contractedPlan.units = [];
      this.setUnit(this.contractedPlan, null, false);
    },
    async loadContractedPlanData() {
      this.contractedPlan = {
        financialGroupId: Number(this.contractedPlanData.financialGroupId),
        contractId: !this.isReuseMovement ? Number(this.contractedPlanData.contractId) : null,
        subContractId: !this.isReuseMovement ? Number(this.contractedPlanData.subContractId) : null,
        planId: !this.isReuseMovement ? Number(this.contractedPlanData.planId) : null,
        startValidityDate: this.formatter.formatDate(this.contractedPlanData.startValidityDate),
        endValidityDate: this.formatter.formatDate(this.contractedPlanData.endValidityDate),
        eligibilityId: !this.isReuseMovement ? this.contractedPlanData.eligibilityId ? Number(this.contractedPlanData.eligibilityId) : null : null,
        familyCode: this.contractedPlanData.familyCode ? this.contractedPlanData.familyCode : null,
        familyCodeSequential: this.contractedPlanData.familyCodeSequential ? this.contractedPlanData.familyCodeSequential : null,
        cancellationReason: this.contractedPlanData.cancellationReason,
        hasGracePeriod: this.contractedPlanData.hasGracePeriod,
        unitId: this.contractedPlanData.unitId ? this.contractedPlanData.unitId : null,
        unitCode: this.contractedPlanData.unitCode ? this.contractedPlanData.unitCode : null,
        unitDescription: this.contractedPlanData.unitDescription ? this.contractedPlanData.unitDescription : null,
        unit: this.contractedPlanData.unit
          ? this.contractedPlanData.unit
          : this.contractedPlanData.unitId
            ? {
              id: this.contractedPlanData.unitId,
              code: this.contractedPlanData.unitCode || null,
              description: this.contractedPlanData.unitDescription || null,
            }
            : null,
        units: [],
      };

      this.isDisabledFamilyCode = this.contractedPlan.familyCode !== null;
      this.isDisabledFamilyCodeSequential = this.contractedPlan.familyCodeSequential !== null;

      if (this.contractedPlan.contractId) {
        const subContractsParams = !this.movementNotAllowed ? "movementNotAllowed=false" : "";

        await this.getContractById(this.contractedPlan.contractId);
        await this.getSubContractsByContractId(this.contractedPlan.contractId, subContractsParams);
      }
      await this.loadUnits();
      await this.loadEligibilities();
    },
    async loadSubContracts() {
      if (this.loadingForm || this.loadingParent) return;
      this.resetUnit();

      if (this.isReuseMovement) {
        this.subContracts = [];
        this.eligibilities = [];
        this.plans = [];
        this.contractedPlan.subContractId = null;
        this.contractedPlan.eligibilityId = null;
        this.contractedPlan.planId = null;
      }

      this.isDisabledSubContracts = true;

      await this.getSubContractsByContractId(this.contractedPlan.contractId);
      if (this.isReuseMovement) {
        this.loadingForm = true;
        const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
        const beneficiarySubContractIds = await this.fetchBeneficiarySubContractIds(movementRecordSession);

        if (movementRecordSession.movementType.includes('INSERT')) {
          this.subContracts = this.subContracts.filter((sub) => !(beneficiarySubContractIds.includes(sub.id)));
        } else {
          this.subContracts = this.subContracts.filter((sub) => beneficiarySubContractIds.includes(sub.id));
        }

        await this.validateAndAssignReusePlanFields();

        this.loadingForm = false;
      }

      if (this.contractedPlan.subContractId) {
        await this.loadUnits();
      }

      this.isDisabledSubContracts = false;
    },
    async validateAndAssignReusePlanFields() {
      const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
      const beneficiaryBenefits = await this.fetchBeneficiaryBenefit(movementRecordSession, this.contractedPlan.contractId);

      if (Array.isArray(beneficiaryBenefits) && beneficiaryBenefits.length === 1) {
        this.subContracts = [];
        this.eligibilities = [];
        this.plans = [];

        const uniqueBenefit = beneficiaryBenefits[0];

        this.contractedPlan.subContractId = uniqueBenefit.subcontractId;
        this.contractedPlan.eligibilityId = uniqueBenefit.eligibilityId;
        this.contractedPlan.planId = uniqueBenefit.planId;

        await this.getSubContractsByContractId(this.contractedPlan.contractId, "onlyMainFields=true");

        if (this.contractedPlan.eligibilityId) {
          await this.getEligibilitiesByContractAndSubcontracts(
            this.contractedPlan.contractId,
            this.contractedPlan.subContractId,
            this.contractedPlan.eligibilityId
          );

          const eligibilityFound = this.eligibilities.find((eligibility) => eligibility.id === this.contractedPlan.eligibilityId);
          await this.getPlansByEligibilityId(eligibilityFound);
        } else {
          await this.preparePlansForReuseByContractAndSubcontract()
        }

        this.subContracts = this.subContracts.filter((sub) => beneficiaryBenefits.some((benefit) => benefit.subcontractId === sub.id));
        this.eligibilities = this.eligibilities.filter((eligibility) => beneficiaryBenefits.some((benefit) => benefit.eligibilityId === eligibility.id));
        this.plans = this.plans.filter((plan) => beneficiaryBenefits.some((benefit) => benefit.planId === plan.id));
      }

      this.$emit("observerContractedPlan", this.contractedPlan)
    },
    async loadEligibilities() {
      if (this.loadingForm || this.loadingParent) return;
      if (this.isReuseMovement) {
        this.contractedPlan.eligibilityId = null;
        this.contractedPlan.planId = null;
        this.eligibilities = [];
        this.plans = [];
      }

      this.isDisabledEligibilites = true;

      if (!this.contractedPlan.subContractId) {
        this.plans = [];
        this.contractedPlan.planId = null;
      } else {
        await this.getEligibilitiesByContractAndSubcontracts(this.contractedPlan.contractId, this.contractedPlan.subContractId, this.contractedPlan.eligibilityId);
        if (!this.isReuseMovement) {
          this.loadPlans();
        } else {
          const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
          const beneficiaryEligibilityIds = await this.fetchBeneficiaryEligibityIds(movementRecordSession);
          this.eligibilities = this.eligibilities.filter((eligibility) => beneficiaryEligibilityIds.includes(eligibility.id));
        }
      }

      if (
        this.isReuseMovement &&
        !this.eligibilities ||
        (Array.isArray(this.eligibilities) && this.eligibilities.length === 0)) {
        await this.loadPlans();
      }

      this.isDisabledEligibilites = false;
    },
    async loadPlans() {
      if (this.loadingForm || this.loadingParent) return;
      if (this.isReuseMovement) {
        this.contractedPlan.planId = null;
      }

      this.isDisabledPlans = true;

      this.plans = [];
      if (this.contractedPlan.eligibilityId) {
        const eligibilityFound = this.eligibilities.find((eligibility) => eligibility.id === this.contractedPlan.eligibilityId);
        this.getPlansByEligibilityId(eligibilityFound);
      } else if (this.contractedPlan.planId) {
        this.getPlanById();
      } else {
        if (!this.isReuseMovement) {
          this.getPlansByAllEligibilities(this.eligibilitiesIds);
        }
        if (this.isReuseMovement) {
          await this.getPlansToReuse();
        }
      }
      if (!this.contractedPlan.eligibilityId && this.isReuseMovement) {
        await this.preparePlansForReuseByContractAndSubcontract()
      }
      this.isDisabledPlans = false;
    },
    async preparePlansForReuseByContractAndSubcontract() {
      const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
      const beneficiaryPlanIds = await this.fetchBeneficiaryPlans(movementRecordSession, this.contractedPlan.contractId, this.contractedPlan.subContractId);
      this.plans = [];

      for (const planId of beneficiaryPlanIds) {
        await this.getPlanByIdMixin(planId);
      }
    },
    async getPlansToReuse() {
      if (this.isReuseMovement) {
        this.loadingForm = true;
        const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
        const beneficiaryPlanIds = await this.fetchBeneficiaryPlans(movementRecordSession, this.contractedPlan.contractId);
        if (movementRecordSession.movementType.includes('INSERT')) {
          this.plans = this.plans.filter((plan) => !(beneficiaryPlanIds.includes(plan.id)));
        } else {
          this.plans = this.plans.filter((plan) => beneficiaryPlanIds.includes(plan.id));
        }
        this.loadingForm = false;
      }

      this.isDisabledPlans = false;
    },
    formatEligibility() {
      if ((this.plans && this.plans.length > 0) && this.contractedPlan.planId) {
        const planFound = this.plans.find((p) => p.id === this.contractedPlan.planId);
        this.contractedPlan.eligibilityPertainingPlan = planFound.eligibility_id;
      }
    },
    observerContractedPlan() {
      this.formatEligibility();
      if (this.isReuseMovement && this.contractedPlan.contractId) {
        const planContract = this.contracts.find((contract) => contract.id === this.contractedPlan.contractId)
        this.contractedPlan.carrierId = planContract.carrier_id.id;
      }
      this.$emit('observerContractedPlan', this.contractedPlan);
    },
    async getPlanById() {
      if (this.contractedPlan.planId) {
        const product2Service = new Product2Service();
        await product2Service.FindById(this.contractedPlan.planId).then((response) => {
          if (response && response.data) {
            const plans = [];
            plans.push(response.data);
            plans.forEach((element) => {
              this.plans.push({
                ...element,
                name: `${element.name} - ${element.code}`,
              });
            });
          }
        });
      }
    },
    saveValidatyDate(validatyDate) {
      if (validatyDate && validatyDate.length === 10) {
        this.$emit('saveValidatyDate', validatyDate);
      }
    },
    captureContractText() {
      let contractFound = '-';
      if ((this.contracts && this.contracts.length > 0) && (this.contractedPlan.contractId)) {
        contractFound = this.contracts.find((contract) => contract.id === Number(this.contractedPlan.contractId));
        if (contractFound && contractFound.text) {
          this.contractName = contractFound.text;
          return contractFound.text;
        }
      }
      return contractFound;
    },
    captureSubContractText() {
      let subContractFound = '-';
      if ((this.subContracts && this.subContracts.length > 0) && (this.contractedPlan.subContractId)) {
        subContractFound = this.subContracts.find((subContract) => subContract.id === Number(this.contractedPlan.subContractId));
        if (subContractFound && subContractFound.text) {
          return subContractFound.text;
        }
      }
      return subContractFound;
    },
    captureEligibilityText() {
      let eligibilityFound = '-';
      if ((this.eligibilities && this.eligibilities.length > 0) && (this.contractedPlan.eligibilityId)) {
        eligibilityFound = this.eligibilities.find((eligibility) => eligibility.id === Number(this.contractedPlan.eligibilityId));
        if (eligibilityFound && eligibilityFound.elegibility_id) {
          return eligibilityFound.elegibility_id.name;
        }
      }
      return eligibilityFound;
    },
    capturePlanText() {
      let planFound = '-';
      if ((this.plans && this.plans.length > 0) && (this.contractedPlan.planId)) {
        planFound = this.plans.find((plan) => plan.id === Number(this.contractedPlan.planId));
        if (planFound) {
          this.planName = planFound.name;
          return planFound.name;
        }
      }
      this.planName = planFound;
      return planFound;
    },
  },

  computed: {
    disabledPlanInput() {
      return (
        this.loadingPlans ||
        this.loadingPlanInformation ||
        this.isDisabledPlans ||
        this.loadingForm ||
        this.isDisabledEligibilites
      ) ||
      (
        !this.contractedPlan.subContractId ||
        (
          this.contractedPlan.subContractId &&
          !this.contractedPlan.eligibilityId &&
          Array.isArray(this.eligibilities) &&
          this.eligibilities.length > 0
        )
      );
    },
    isDisabledUnitField() {
      return (
        this.loadingPlanInformation ||
        this.loadingUnits ||
        this.loadingForm ||
        (this.isDetail && !this.isReuseMovement) ||
        !this.contractedPlan.subContractId
      );
    },
  },

  created() {
    this.rule = new Rules();
    this.formatter = new Formatters();
    this.carrierService = new CarrierService();
    this.contractUnitService = new ContractUnitService();
  },

  async mounted() {
    this.loadingPlanInformation = true;
    if (this.isReuseMovement) {
      this.loadingForm = true;
      const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
      await this.getContractsByFinancialGroupId(movementRecordSession.financialGroupId);
      const beneficiaryContractIds = await this.fetchBeneficiaryContractIds(movementRecordSession);
      if (movementRecordSession.movementType.includes('INSERT')) {
        this.contracts = this.contracts.filter((contract) => !(beneficiaryContractIds.includes(contract.id)));
      } else {
        this.contracts = this.contracts.filter((contract) => beneficiaryContractIds.includes(contract.id));
      }
      this.loadingForm = false;
    }
    this.loadingPlanInformation = false;
  }
};
</script>
